import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { type RootState } from "app";
import { initScene, renderActions, resetView, setViewpoint } from "features/render/renderSlice";
import { ObjectVisibility } from "features/render/types";
import { capitalize } from "utils/misc";

export enum Sort {
    Ascending,
    Descending,
    None,
}

export interface SelectedProperty {
    searchPropertyName: string;
    propertyValue: string;
}

const initialState = {
    stampSettings: {
        enabled: false,
    },
    starred: {} as Record<string, true | undefined>,
    showStamp: false,
    sort: Sort.None,
    /** Properties checked in the Properties window, keyed by {@link propertyKey}. */
    selectedProperties: {} as Record<string, SelectedProperty>,
    /**
     * The highlighted set from just before the first box was checked. Checking replaces
     * the selection with the filter's search result, so clearing the last box hands the
     * user their own selection back instead of leaving them with nothing.
     */
    preFilterSelection: undefined as number[] | undefined,
    /**
     * `mainObjects` from just before the first box was checked. The filter replaces the
     * semantic selection with match IDs; clearing the last box has to hand that set back
     * the same way `preFilterSelection` hands back the highlight.
     */
    preFilterMainObjects: undefined as number[] | undefined,
};

type State = typeof initialState;

/**
 * The whole checkbox filter. The checked rows and the selection they replaced are
 * captured and restored together — an undo that put back one without the other would
 * leave the filter describing a selection that is gone.
 */
export type PropertyFilterSnapshot = {
    selectedProperties: Record<string, SelectedProperty>;
    preFilterSelection: number[] | undefined;
    preFilterMainObjects: number[] | undefined;
};

/** Read a {@link PropertyFilterSnapshot} off the store, for an action's undo to restore. */
export function capturePropertyFilter(state: RootState): PropertyFilterSnapshot {
    return {
        selectedProperties: selectSelectedProperties(state),
        preFilterSelection: selectPreFilterSelection(state),
        preFilterMainObjects: selectPreFilterMainObjects(state),
    };
}

/** End the filter. The checked rows and the selection they replaced go together. */
function clearFilter(state: State): void {
    state.selectedProperties = initialState.selectedProperties;
    state.preFilterSelection = initialState.preFilterSelection;
    state.preFilterMainObjects = initialState.preFilterMainObjects;
}

/**
 * Identity of one checkbox. The value is part of it because a group can list the same
 * property name more than once with different values — neither the object data nor the
 * IFC key transform dedupes — and each of those rows has to toggle on its own.
 */
export const propertyKey = (groupName: string, propertyName: string, propertyValue: string) =>
    JSON.stringify([groupName, propertyName, propertyValue]);

const searchPropertyName = (groupName: string, propertyName: string, isGeneralGroup: boolean) =>
    isGeneralGroup || !groupName ? propertyName : `${groupName}/${propertyName}`;

export const propertiesSlice = createSlice({
    name: "properties",
    initialState: initialState,
    reducers: {
        setStampSettings: (state, action: PayloadAction<Partial<State["stampSettings"]>>) => {
            state.stampSettings = { ...state.stampSettings, ...action.payload };
        },
        toggleEnableStamp: (state, action: PayloadAction<State["stampSettings"]["enabled"] | undefined>) => {
            const toggled = action.payload !== undefined ? action.payload : !state.stampSettings.enabled;
            state.stampSettings.enabled = toggled;
            state.showStamp = toggled;
        },
        toggleShowStamp: (state, action: PayloadAction<State["showStamp"] | undefined>) => {
            state.showStamp = action.payload !== undefined ? action.payload : !state.showStamp;
        },
        setStarred: (state, action: PayloadAction<State["starred"]>) => {
            state.starred = action.payload;
        },
        star: (state, action: PayloadAction<string>) => {
            state.starred[action.payload] = true;
        },
        unStar: (state, action: PayloadAction<string>) => {
            delete state.starred[action.payload];
        },
        toggleSort: (state) => {
            state.sort =
                state.sort === Sort.None
                    ? Sort.Descending
                    : state.sort === Sort.Descending
                      ? Sort.Ascending
                      : Sort.None;
        },
        togglePropertySelection: (
            state,
            action: PayloadAction<{
                groupName: string;
                propertyName: string;
                propertyValue: string;
                isGeneralGroup: boolean;
            }>,
        ) => {
            const { groupName, propertyName, propertyValue, isGeneralGroup } = action.payload;
            const key = propertyKey(groupName, propertyName, propertyValue);

            if (key in state.selectedProperties) {
                delete state.selectedProperties[key];
            } else {
                state.selectedProperties[key] = {
                    searchPropertyName: searchPropertyName(groupName, propertyName, isGeneralGroup),
                    propertyValue,
                };
            }
        },
        toggleGroupSelection: (
            state,
            action: PayloadAction<{
                groupName: string;
                properties: [string, string][];
                isGeneralGroup: boolean;
            }>,
        ) => {
            const { groupName, properties, isGeneralGroup } = action.payload;

            if (properties.length === 0) {
                return;
            }

            const isFullySelected = properties.every(
                ([propertyName, propertyValue]) =>
                    propertyKey(groupName, propertyName, propertyValue) in state.selectedProperties,
            );

            for (const [propertyName, propertyValue] of properties) {
                const key = propertyKey(groupName, propertyName, propertyValue);

                if (isFullySelected) {
                    delete state.selectedProperties[key];
                } else {
                    state.selectedProperties[key] = {
                        searchPropertyName: searchPropertyName(groupName, propertyName, isGeneralGroup),
                        propertyValue,
                    };
                }
            }
        },
        setPropertyFilter: (state, action: PayloadAction<PropertyFilterSnapshot>) => {
            state.selectedProperties = action.payload.selectedProperties;
            state.preFilterSelection = action.payload.preFilterSelection;
            state.preFilterMainObjects = action.payload.preFilterMainObjects;
        },
        clearPropertySelection: clearFilter,
    },
    extraReducers(builder) {
        builder.addCase(initScene, (state, action) => {
            const props = action.payload.sceneData.customProperties;

            if (props.explorerProjectState?.features?.properties?.starred) {
                state.starred = Object.fromEntries(
                    props.explorerProjectState.features.properties.starred.map((prop) => [prop, true]),
                );
            } else if (props.properties) {
                state.starred = Object.fromEntries(
                    props.properties.starred.map((prop: string) => [
                        ["path", "name"].includes(prop) ? capitalize(prop) : prop,
                        true,
                    ]),
                );
            }

            state.stampSettings =
                props.explorerProjectState?.features?.properties?.stamp ??
                props.properties?.stampSettings ??
                state.stampSettings;

            state.showStamp = state.stampSettings.enabled;
        });
        builder.addCase(resetView, clearFilter);
        builder.addCase(setViewpoint, (state, action) => {
            // A partial apply leaves the highlighted set alone, and these checkboxes mirror that set.
            if (action.meta.ignoreUndefined) {
                return;
            }

            clearFilter(state);
        });
        // Checking a property is what turns X-ray on, so leaving X-ray by any other route
        // ends the filter the checkboxes describe. Entering it is left alone — that is the
        // transition a check itself makes.
        builder.addCase(renderActions.setVisibilityMode, (state, action) => {
            if (action.payload !== ObjectVisibility.Xray) {
                clearFilter(state);
            }
        });
        // `setVisibilityMode` and `setDefaultVisibility` are independent sibling reducers
        // (neither wraps the other — see the README comment on `setVisibilityMode` in
        // renderSlice), and the many callers that leave X-ray via bookmarks, BIMcollab/BIM
        // Track, clash, forms and the selection basket all go through this one.
        builder.addCase(renderActions.setDefaultVisibility, (state, action) => {
            if (action.payload !== ObjectVisibility.Xray) {
                clearFilter(state);
            }
        });
    },
});

export const selectPropertiesSort = (state: RootState) => state.properties.sort;
export const selectPropertiesStampSettings = (state: RootState) => state.properties.stampSettings;
export const selectShowPropertiesStamp = (state: RootState) => state.properties.showStamp;
export const selectStarredProperties = (state: RootState) => state.properties.starred;
export const selectSelectedProperties = (state: RootState) => state.properties.selectedProperties;
export const selectPreFilterSelection = (state: RootState) => state.properties.preFilterSelection;
export const selectPreFilterMainObjects = (state: RootState) => state.properties.preFilterMainObjects;
export const selectPropertiesSettings = createSelector(
    (state: RootState) => state.properties.stampSettings.enabled,
    (state: RootState) => state.properties.starred,
    (stampEnabled, starred) => ({
        stamp: {
            enabled: stampEnabled,
        },
        starred: Object.keys(starred),
    }),
);

const { actions, reducer } = propertiesSlice;
export { actions as propertiesActions, reducer as propertiesReducer };
